import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { HbarReleaseTopBarBadge } from '../hedera/HbarReleaseTopBarBadge';
import { AnimatedLogo } from '../AnimatedLogo';
import { Tooltip } from '../ui/Tooltip';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { UserMenu } from '../auth/UserMenu';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useToast } from '../Toast';
import { exportBackup, importBackup, getImportSummary, isPortfolioEmpty } from '../../services/backupService';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePageModules } from '../../hooks/usePageModules';
import { useAuthStore } from '../../store/authStore';
import type { CalculationMethod } from '../../types';
import type { UserMode } from '../../hooks/useUserMode';

interface TopBarProps {
  currentPage: string;
  calculationMethod?: CalculationMethod;
  onCalculationMethodChange?: (method: CalculationMethod) => void;
  showCalculationSelector?: boolean;
  onToggleSidebar?: () => void;
  onNavigateHome?: () => void;
  userMode?: UserMode;
  onUserModeChange?: (mode: UserMode) => void;
  onShowAlertPanel?: () => void;
}

export function TopBar({
  currentPage,
  calculationMethod = 'FIFO',
  onCalculationMethodChange,
  showCalculationSelector = false,
  onToggleSidebar,
  onNavigateHome,
  userMode = 'simple',
  onUserModeChange,
  onShowAlertPanel
}: TopBarProps) {
  const isMobile = useIsMobile();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { language } = useLanguage(); // Force re-render on language change
  const { isModuleEnabled } = usePageModules();
  const { isAuthenticated } = useAuthStore();

  // Handle logo click - use onNavigateHome callback (same as sidebar)
  const handleLogoClick = () => {
    if (onNavigateHome) {
      onNavigateHome();
    }
  };

  // Check if Dashboard iPad module is enabled
  const isDashboardIpadEnabled = isModuleEnabled('dashboard-ipad');

  // Handle smart backup button click
  const handleBackupClick = () => {
    if (isPortfolioEmpty()) {
      // Portfolio is empty - trigger import
      fileInputRef.current?.click();
    } else {
      // Portfolio has data - trigger export
      handleExportJSON();
    }
  };

  // Export JSON using shared service
  const handleExportJSON = () => {
    const result = exportBackup();
    if (result.success) {
      toast(`✅ ${t('topBar.backupExported')}: ${result.summary}`, 'success');
    } else {
      toast(t('topBar.exportError'), 'error');
    }
  };

  // Handle file import using shared service
  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    importBackup(
      file,
      (result) => {
        const summary = getImportSummary(result.counts);
        toast(`✅ ${t('topBar.backupImported')}: ${summary}`, 'success');

        // Reload page to apply changes
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      },
      (errorMessage) => {
        toast(errorMessage, 'error');
      }
    );

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <header className="top-bar">
      {/* Left side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {/* Mobile: Logo + Menu button */}
        {isMobile && (
          <>
            {/* App Logo - Icon only (text shown in sidebar when open) */}
            <div
              onClick={handleLogoClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              title="Edouard.ai"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleLogoClick()}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <AnimatedLogo size={22} variant="draw-dot" color="white" glowColor="#a78bfa" duration={2} />
              </div>
            </div>

            {/* Hamburger menu */}
            {onToggleSidebar && (
              <button
                onClick={onToggleSidebar}
                aria-label="Toggle menu"
                title="Toggle menu"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text)',
                }}
              >
                <Icons.Menu size={24} />
              </button>
            )}
          </>
        )}

        {/* Desktop: Breadcrumbs */}
        {!isMobile && (
          <div className="breadcrumbs">
            <span className="breadcrumb-item">
              <Icons.Home size={16} />
            </span>
            <span className="breadcrumb-separator">
              <Icons.ChevronRight size={14} />
            </span>
            <span className="breadcrumb-item active">
              {currentPage}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="top-bar-actions">
        {/* Calculation Method Selector (only shown on Portfolio page in Expert mode) */}
        {showCalculationSelector && onCalculationMethodChange && userMode === 'expert' && (
          <Tooltip
            content={
              <div>
                <strong>{t('topBar.calculationMethod')}</strong>
                <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: '1.6' }}>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>{t('topBar.fifo')}:</strong> {t('topBar.fifoDesc')}
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>{t('topBar.lifo')}:</strong> {t('topBar.lifoDesc')}
                  </div>
                  <div>
                    <strong>{t('topBar.averageCost')}:</strong> {t('topBar.averageCostDesc')}
                  </div>
                </div>
              </div>
            }
            position="bottom"
            delay={400}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '12px' }}>
              <Icons.Calculator size={16} style={{ color: 'var(--muted)' }} />
              <select
                value={calculationMethod}
                onChange={(e) => onCalculationMethodChange(e.target.value as CalculationMethod)}
                aria-label="Calculation method"
                style={{
                  padding: '6px 10px',
                  fontSize: '13px',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
                title={t('topBar.calculationMethod')}
              >
                <option value="FIFO">{t('topBar.fifo')}</option>
                <option value="LIFO">{t('topBar.lifo')}</option>
                <option value="AVERAGE_COST">{t('topBar.averageCost')}</option>
              </select>
            </div>
          </Tooltip>
        )}

        {/* Search bar (hidden on mobile) */}
        {!isMobile && (
          <div className="search-bar">
            <Icons.Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search... (⌘K)"
              className="search-input"
              readOnly
              onClick={() => {
                // Will trigger command palette
                const event = new KeyboardEvent('keydown', {
                  key: 'k',
                  metaKey: true,
                  bubbles: true
                });
                window.dispatchEvent(event);
              }}
            />
            <kbd className="kbd">⌘K</kbd>
          </div>
        )}

        {/* iPad Dashboard Link - Only show if module is enabled and NOT on mobile */}
        {isDashboardIpadEnabled && !isMobile && (
          <Tooltip
            content={
              <div>
                <strong>{t('moduleHub.modules.dashboardIpad.label')}</strong>
                <div style={{ marginTop: '4px', fontSize: '12px' }}>
                  {t('moduleHub.modules.dashboardIpad.description')}
                </div>
              </div>
            }
            position="bottom"
            delay={300}
          >
            <Link
              to="/dashboard-ipad"
              className="icon-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
              }}
              title={t('moduleHub.modules.dashboardIpad.label')}
            >
              <Icons.Tablet size={20} />
            </Link>
          </Tooltip>
        )}

        {/* V2.J.3 — HBAR treasury release badge. Renders nothing
            unless a release was detected in the last 7 days AND the
            user hasn't already dismissed it for the current event. */}
        <HbarReleaseTopBarBadge />

        {/* Alert Bell - Only shown when authenticated */}
        {isAuthenticated && onShowAlertPanel && (
          <button
            className="icon-btn"
            onClick={onShowAlertPanel}
            aria-label={t('alerts.priceAlerts', 'Alertes Prix')}
          >
            <Icons.Bell size={20} />
          </button>
        )}

        {/* Language Switcher - Hidden on mobile (shown in sidebar footer instead) */}
        {!isMobile && <LanguageSwitcher />}

        {/* Smart Backup Button - Only shown when NOT authenticated (hidden on mobile) */}
        {!isMobile && !isAuthenticated && (
          <Tooltip
            content={
              <div>
                <strong>{t('topBar.smartBackup')}</strong>
                <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: '1.6' }}>
                  {isPortfolioEmpty() ? (
                    <div>📥 {t('topBar.importBackup')}</div>
                  ) : (
                    <div>📤 {t('topBar.exportPortfolio')}</div>
                  )}
                </div>
              </div>
            }
            position="bottom"
            delay={300}
          >
            <button
              className="icon-btn"
              onClick={handleBackupClick}
              aria-label={isPortfolioEmpty() ? "Importer un backup" : "Exporter le portfolio"}
              title={isPortfolioEmpty() ? "Importer un backup" : "Exporter le portfolio"}
              style={{
                position: 'relative'
              }}
            >
              {isPortfolioEmpty() ? (
                <Icons.Download size={20} />
              ) : (
                <Icons.Save size={20} />
              )}
            </button>
          </Tooltip>
        )}

        {/* User Menu (Login/Signup or User Profile) - Always at the far right */}
        <UserMenu
          userMode={userMode}
          onUserModeChange={isAuthenticated ? onUserModeChange : undefined}
        />

        {/* Hidden file input for import */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileImport}
          hidden
        />
      </div>
    </header>
  );
}
